import {
  FOC_TOUR_LABEL_CMD_IC,
  FOC_TOUR_LABEL_CMD_LEGAL,
  FOC_TOUR_LABEL_CMD_LIAISON,
  FOC_TOUR_LABEL_CMD_PIO,
  FOC_TOUR_LABEL_CMD_SAFETY,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import { FOC_REQUIRED_DISPOSITION } from '../orgWorkspaceTour/orgWorkspaceTourSteps';

export type FocCmdTourStepDef = {
  id: string;
  requirementId?: string;
  title: string;
  disposition?: string;
  description: string;
  bullets?: string[];
  tour?: string;
};

type RequirementInput = Omit<FocCmdTourStepDef, 'title' | 'requirementId'> & {
  requirementId: string;
  name: string;
};

function requirement({
  requirementId,
  name,
  ...rest
}: RequirementInput): FocCmdTourStepDef {
  return {
    ...rest,
    requirementId,
    title: `${requirementId} — ${name}`,
  };
}

export function focCmdTourNavVisible(enabled: boolean): boolean {
  return enabled;
}

export function focCmdTourStepIds(steps: FocCmdTourStepDef[]): string[] {
  return steps.map((step) => step.id);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function focCmdTourPopoverHtml(
  step: Pick<FocCmdTourStepDef, 'disposition' | 'description' | 'bullets'>
): string {
  const body = `<p>${escapeHtml(step.description)}</p>`;
  const bullets = step.bullets?.length
    ? `<ul>${step.bullets
        .map((bullet) => `<li>${escapeHtml(bullet)}</li>`)
        .join('')}</ul>`
    : '';
  const content = `${body}${bullets}`;
  if (!step.disposition) return content;
  return `<p><strong>Disposition:</strong> ${escapeHtml(step.disposition)}</p>${content}`;
}

export const FOC_CMD_IC_TOUR_STEPS: FocCmdTourStepDef[] = [
  {
    id: 'sectionIntro',
    title: FOC_TOUR_LABEL_CMD_IC,
    description:
      'PRATUS provides an Incident Command and Unified Command workspace for authorized command personnel. This seat is the persistent role workspace for IC/UC product work; Planning-P meetings coordinate the cycle but do not replace create, revise, submit, review, approve, and manage here.',
  },
  requirement({
    id: 'cmd001',
    requirementId: 'FOC-CMD-001',
    name: 'Incident Command / Unified Command Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'Authorized command personnel use this workspace to view and manage incident priorities, objectives, command decisions, organizational authorities, Critical Information Requirements, approvals, open actions, incident status, resource constraints, safety considerations, and the operational plan — via related forms, tasks, and linked incident products.',
  }),
  requirement({
    id: 'cmd001a',
    requirementId: 'FOC-CMD-001a',
    name: 'Command Decision Record',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Command decisions from initial response, Initial Unified Command Meeting, Objectives Meeting, Planning Meeting, IAP approval, and other authorized command events are recorded through meeting facilitation, form history, and product versions. This walkthrough does not open a separate decision-log panel; those surfaces remain the system of record.',
  }),
  requirement({
    id: 'cmd001b',
    requirementId: 'FOC-CMD-001b',
    name: 'Approval Visibility',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-forms',
    description:
      'Forms exposes products requiring command review or approval for this seat — including ICS 202, ICS 203, and related IAP components. Draft and final IAPs, selected supplemental plans, resource-related decisions, and financial summaries surface through authorized products and Planning-P status as applicable.',
  }),
  requirement({
    id: 'cmd001c',
    requirementId: 'FOC-CMD-001c',
    name: 'Demobilization Oversight',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Command visibility into demobilization priorities, critical-resource release decisions, Demobilization Plan status, outstanding closeout actions, hotwash actions, and after-action requirements is coordinated through Planning-P products, tasks, and authorized demobilization workflows. This seat walkthrough does not open a dedicated demob command board.',
  }),
  requirement({
    id: 'cmd001d',
    requirementId: 'FOC-CMD-001d',
    name: 'Command Decision and Open-Action Visibility',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-tasks',
    description:
      'Tasks and linked Actions keep command decisions, Critical Information Requirements, limitations/constraints, unresolved and delegated actions, due dates, responsible positions, and decision status visible. Approved command decisions populate applicable Planning-P products and remain protected from unauthorized modification.',
  }),
  requirement({
    id: 'cmd001e',
    requirementId: 'FOC-CMD-001e',
    name: 'Command Handover and Delegation',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'When command transfers, Unified Command membership changes, or approval authority is delegated, PRATUS records effective time, outgoing and incoming authority, affected approvals, unresolved actions, and required notifications through roster and history surfaces while preserving prior command history. This walkthrough does not execute a live handover.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_CMD_IC),
  },
];

export const FOC_CMD_SAFETY_TOUR_STEPS: FocCmdTourStepDef[] = [
  {
    id: 'sectionIntro',
    title: FOC_TOUR_LABEL_CMD_SAFETY,
    description:
      'PRATUS provides a Safety Officer workspace for incident safety concerns, hazards, mitigations, safety messages, ICS 208 and ICS 208HM, ICS 215A contributions, safety approvals, safety-related action items, and applicable Operational Brief content.',
  },
  requirement({
    id: 'cmd002',
    requirementId: 'FOC-CMD-002',
    name: 'Safety Officer Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'This persistent Safety Officer seat is where authorized users manage safety products and action items for the incident. Related forms include ICS 208, ICS 208HM, and ICS 215A; tasks capture safety-related work status.',
  }),
  requirement({
    id: 'cmd002a',
    requirementId: 'FOC-CMD-002a',
    name: 'Safety Linkages',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Safety concerns and mitigations link to applicable objectives, tactics, work assignments, operational areas, resources, map features, ICS 215A entries, ICS 204 special instructions, and IAP components through shared product fields — avoiding duplicate entry where fields are equivalent. COP map surfaces are covered in SITL / Hub COP tours.',
  }),
  requirement({
    id: 'cmd002b',
    requirementId: 'FOC-CMD-002b',
    name: 'Safety Hazard and Mitigation Management',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-forms',
    description:
      'Forms provides the persistent view of safety products that carry identified hazards, risk assessments, mitigations, responsible positions, affected branches/divisions/groups, status, source, and operational-period applicability. Hazard and mitigation data feeds ICS 215A, ICS 208/208HM, ICS 204 special instructions, COP displays, notifications, and safety brief products.',
  }),
  requirement({
    id: 'cmd002c',
    requirementId: 'FOC-CMD-002c',
    name: 'Stop-Work / Stand-Down Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized Safety/Command users can initiate the approved Stand Down Safety notification workflow with deliberate confirmation, initiator and time recorded, authorized delivery channels, and acknowledgement/response status preserved. This walkthrough does not initiate a Stand Down.',
  }),
  requirement({
    id: 'cmd002d',
    requirementId: 'FOC-CMD-002d',
    name: 'Incident Mishap Workflow / ICS 237',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'PRATUS provides a controlled incident mishap reporting workflow that can produce the current approved ICS 237-CG when applicable — with required fields, authorized attachments/evidence, responsible-user and date/time history, restricted access where needed, and source-faithful export. Field-level implementation is validated against the current approved form before acceptance; this walkthrough notes coverage without opening the mishap builder.',
  }),
  requirement({
    id: 'cmd002e',
    requirementId: 'FOC-CMD-002e',
    name: 'Safety Product Status',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-forms',
    description:
      'Forms status shows which required safety products are not started, in progress, ready for review, approved, superseded, or complete for the current and next operational periods.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_CMD_SAFETY),
  },
];

export const FOC_CMD_LIAISON_TOUR_STEPS: FocCmdTourStepDef[] = [
  {
    id: 'sectionIntro',
    title: FOC_TOUR_LABEL_CMD_LIAISON,
    description:
      'PRATUS provides a Liaison Officer workspace for managing incident participation by external agencies, stakeholders, partner organizations, agency representatives, external users, and other authorized incident participants.',
  },
  requirement({
    id: 'cmd003',
    requirementId: 'FOC-CMD-003',
    name: 'Liaison Officer Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'This Liaison Officer seat is the persistent workspace for coordinating external participation. Related organization products (including ICS 203) and tasks support partner coordination without granting Coast Guard resource authority to external participants.',
  }),
  requirement({
    id: 'cmd003a',
    requirementId: 'FOC-CMD-003a',
    name: 'External User Management',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-tasks',
    description:
      'Authorized users track external-user identity and organization, incident role or representation, workspace access, onboarding status, access-expiration date or trigger, demobilization status, incident closure status, applicable access restrictions, coordination notes, external resources offered or identified, and unresolved partner-access issues — via Tasks, roster/access controls, and related incident admin surfaces.',
  }),
  requirement({
    id: 'cmd003b',
    requirementId: 'FOC-CMD-003b',
    name: 'External Resource Contribution',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Authorized external participants can identify resources they own or can provide without automatically receiving authority to request, order, allocate, approve, or access Coast Guard resources.',
  }),
  requirement({
    id: 'cmd003c',
    requirementId: 'FOC-CMD-003c',
    name: 'Agency Representative and Partner Tracking',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-forms',
    description:
      'Forms and related organization products help maintain participating agencies/organizations, Agency Representatives, contact information, role, assistance/cooperation status, key issues, requests, commitments, meeting participation, external-user access status, and unresolved liaison actions.',
  }),
  requirement({
    id: 'cmd003d',
    requirementId: 'FOC-CMD-003d',
    name: 'Partner Issue and Commitment Tracking',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-tasks',
    description:
      'The Liaison Officer creates and tracks interagency issues, commitments, decisions, requested actions, responsible party, due date, status, and disposition through Tasks and linked Actions, and can surface approved items to Command, Planning, Logistics, Operations, or the Situation Unit as authorized.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_CMD_LIAISON),
  },
];

export const FOC_CMD_PIO_TOUR_STEPS: FocCmdTourStepDef[] = [
  {
    id: 'sectionIntro',
    title: FOC_TOUR_LABEL_CMD_PIO,
    description:
      'PRATUS provides a Public Information Officer workspace for coordinating approved incident information, public-information products, media-related action items, communications dependencies, stakeholder information requirements, and IAP or Operations Brief contributions. PRATUS does not automatically release, publish, or distribute public-information products without authorized human approval.',
  },
  requirement({
    id: 'cmd004',
    requirementId: 'FOC-CMD-004',
    name: 'Public Information Officer Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'This PIO seat is the persistent workspace for public-information coordination. Related forms (including ICS 202) and tasks support product work and media-related actions under human approval controls.',
  }),
  requirement({
    id: 'cmd004a',
    requirementId: 'FOC-CMD-004a',
    name: 'Public Information Product Workflow',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-forms',
    description:
      'Forms and product lifecycle support drafting, review, coordination, approval, release status, versioning, and archive of authorized public-information products. Workflow metadata identifies intended audience, release channel, approving authority, release time, and source information used.',
  }),
  requirement({
    id: 'cmd004b',
    requirementId: 'FOC-CMD-004b',
    name: 'Public Information Coordination',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-tasks',
    description:
      'Tasks track Joint Information Center / Joint Information System coordination, media/public inquiries, rumor or misinformation issues, scheduled releases/briefings, talking points, and unresolved public-information actions when activated.',
  }),
  requirement({
    id: 'cmd004c',
    requirementId: 'FOC-CMD-004c',
    name: 'Operational-to-Public Information Separation',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'PRATUS separates internal operational information from content approved for public release. External/public-facing output is not automatically generated from restricted incident content and requires the applicable review and release authority.',
  }),
  requirement({
    id: 'cmd004d',
    requirementId: 'FOC-CMD-004d',
    name: 'PIO Access to Approved Operational Picture',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'The PIO workspace provides permission-aware access to approved incident facts, maps, status, objectives, safety information, and partner/public messaging inputs needed for public-information work — without exposing restricted information. Position Workspaces and Planning-P keep facilitation and approved picture access connected under authorization.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_CMD_PIO),
  },
];

export const FOC_CMD_LEGAL_TOUR_STEPS: FocCmdTourStepDef[] = [
  {
    id: 'sectionIntro',
    title: FOC_TOUR_LABEL_CMD_LEGAL,
    description:
      'PRATUS provides a role-aware Legal Officer workspace for authorized legal review, legal action tracking, incident-product review, and support to Command and General Staff functions, consistent with assigned authority and applicable access controls. The workspace does not assign legal authorities, provide legal conclusions, or replace legal review.',
  },
  requirement({
    id: 'cmd005',
    requirementId: 'FOC-CMD-005',
    name: 'Legal Officer Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-root',
    description:
      'This Legal Officer seat is a persistent, incident-specific workspace under access controls. This seat has no default related ICS forms; legal product review and action tracking use Tasks and authorized review workflows rather than a forms catalog on the seat home.',
  }),
  requirement({
    id: 'cmd005a',
    requirementId: 'FOC-CMD-005a',
    name: 'Legal Issue and Review Tracking',
    disposition: FOC_REQUIRED_DISPOSITION,
    tour: 'position-workspace-tasks',
    description:
      'Authorized users record legal issues or review requests — requesting position, affected incident decision/product, priority, status, responsible legal reviewer, advice/decision status, and completion time — without requiring sensitive legal analysis to be visible outside authorized legal users.',
  }),
  requirement({
    id: 'cmd005b',
    requirementId: 'FOC-CMD-005b',
    name: 'Legal Review Dependencies',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'PRATUS can identify when an operational product or decision is awaiting legal review and notify responsible users without exposing privileged or otherwise restricted legal content to users who lack access. Notifications and product status convey dependency without revealing privileged analysis.',
  }),
  requirement({
    id: 'cmd005c',
    requirementId: 'FOC-CMD-005c',
    name: 'Legal Workspace Validation',
    disposition: FOC_REQUIRED_DISPOSITION,
    description:
      'Detailed Legal Officer workflows, records handling, privilege controls, and product requirements require Coast Guard legal SME and information-governance validation before acceptance. PRATUS shall not infer legal authority or provide autonomous legal determinations.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_CMD_LEGAL),
  },
];
